import { Badge, Modal, Spinner, Table } from "react-bootstrap";
import { FaCreditCard, FaFileInvoice } from "react-icons/fa";
import { useSubscription } from "../../hooks/useSubscription";

/**
 * Modal de histórico de pagamentos
 */
export const PaymentHistoryModal = ({ show, onHide }) => {
  const { payments, isLoadingPayments } = useSubscription();

  // Função para converter string de data YYYY-MM-DD sem problemas de timezone
  const parseDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-');
    return new Date(year, month - 1, day);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      PENDING: { variant: 'warning', label: 'Pendente' },
      CONFIRMED: { variant: 'info', label: 'Confirmado' },
      RECEIVED: { variant: 'success', label: 'Recebido' },
      OVERDUE: { variant: 'danger', label: 'Vencido' },
      REFUNDED: { variant: 'secondary', label: 'Reembolsado' },
      RECEIVED_IN_CASH: { variant: 'success', label: 'Pago' },
      REFUND_REQUESTED: { variant: 'warning', label: 'Reembolso Solicitado' },
      CHARGEBACK_REQUESTED: { variant: 'warning', label: 'Chargeback' },
      CHARGEBACK_DISPUTE: { variant: 'warning', label: 'Em Disputa' },
      AWAITING_CHARGEBACK_REVERSAL: { variant: 'info', label: 'Aguardando Reversão' },
    };

    const config = statusMap[status] || { variant: 'secondary', label: status };
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  const paymentsList = payments?.payments || [];
  const totalCount = payments?.totalCount || 0;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton style={{ borderBottom: '2px solid #e9ecef' }}>
        <Modal.Title>
          <FaCreditCard className="me-2 text-primary" />
          Histórico de Pagamentos
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', padding: '1.5rem' }}>
        {isLoadingPayments ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="warning" />
            <p className="mt-3">Carregando histórico de pagamentos...</p>
          </div>
        ) : (
          <>
            {/* Info Header */}
            <div className="mb-3">
              <p className="text-muted mb-3">
                Total de <strong>{totalCount}</strong> pagamento{totalCount !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Tabela de Pagamentos */}
            {paymentsList.length === 0 ? (
              <div className="text-center py-5">
                <FaFileInvoice size={64} className="text-muted mb-3" />
                <h5>Nenhum pagamento encontrado</h5>
                <p className="text-muted">
                  Você ainda não possui histórico de pagamentos
                </p>
              </div>
            ) : (
              <>
                <div style={{ 
                  border: '1px solid #e9ecef', 
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <Table responsive hover className="mb-0">
                    <thead style={{ background: '#f8f9fa' }}>
                      <tr>
                        <th className="p-3">Data de Vencimento</th>
                        <th className="p-3">Valor</th>
                        <th className="p-3">Status</th>
                        <th className="p-3">Data de Pagamento</th>
                        <th className="p-3 text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentsList.map((payment) => (
                        <tr key={payment.id}>
                          <td className="p-3">
                            {parseDate(payment.dueDate).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </td>
                          <td className="p-3">
                            <strong>
                              R$ {parseFloat(payment.value).toFixed(2)}
                            </strong>
                          </td>
                          <td className="p-3">
                            {getStatusBadge(payment.status)}
                          </td>
                          <td className="p-3">
                            {payment.paymentDate ? (
                              parseDate(payment.paymentDate).toLocaleDateString('pt-BR', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {payment.invoiceUrl && (
                              <a
                                href={payment.invoiceUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-sm btn-outline-primary"
                              >
                                <FaFileInvoice className="me-1" />
                                Ver Fatura
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Informações Adicionais */}
                <div className="row mt-4 g-3">
                  <div className="col-md-6">
                    <div style={{ 
                      background: '#e7f3ff', 
                      padding: '1rem', 
                      borderRadius: '8px',
                      border: '1px solid #b3d9ff'
                    }}>
                      <h6 className="fw-bold mb-2" style={{ fontSize: '0.9rem' }}>
                        💡 Informações
                      </h6>
                      <small className="text-muted">
                        Os pagamentos são processados automaticamente no vencimento.
                        Em caso de dúvidas, entre em contato com o suporte.
                      </small>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div style={{ 
                      background: '#fff3cd', 
                      padding: '1rem', 
                      borderRadius: '8px',
                      border: '1px solid #ffeaa7'
                    }}>
                      <h6 className="fw-bold mb-2" style={{ fontSize: '0.9rem' }}>
                        ⚠️ Pagamentos Pendentes
                      </h6>
                      <small className="text-muted">
                        Pagamentos com status "Pendente" ou "Vencido" podem afetar
                        o acesso aos recursos da plataforma.
                      </small>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};
